import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ModalProvider } from './contexts/ModalContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Hero from './components/Hero';
import About from './components/About';
import Products from './components/Products';
import Pricing from './components/Pricing';
import AskForAccess from './components/AskForAccess';
import Chatbot from './components/Chatbot';
import ScrollToTop from './components/ScrollToTop';
import IndustryPage from './components/IndustryPage';
import TryItYourself from './components/TryItYourself';
import ProductPage from './components/ProductPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import BusinessContinuityPolicy from './components/BusinessContinuityPolicy';
import Preloader from './components/Preloader';
import { initDisplayScale } from './utils/displayScale';
import './index.css';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    // Initialize display scale detection for consistent styling across all display scales
    initDisplayScale();

    // Function to wait for all critical resources to load with progress tracking
    const waitForResources = async () => {
      try {
        setLoadingProgress(10);

        // Wait for document to be ready
        if (document.readyState !== 'complete') {
          await new Promise((resolve) => {
            window.addEventListener('load', resolve, { once: true });
          });
        }
        setLoadingProgress(30);

        // Wait for critical images to load
        const criticalImages = [
          '/hacker.png',
          '/homepagevector.png',
        ];

        const imagePromises = criticalImages.map((src, index) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              setLoadingProgress(30 + ((index + 1) / criticalImages.length) * 30);
              resolve(null);
            };
            img.onerror = () => {
              setLoadingProgress(30 + ((index + 1) / criticalImages.length) * 30);
              resolve(null);
            };
            img.src = src;
            // Timeout after 3 seconds
            setTimeout(() => {
              setLoadingProgress(30 + ((index + 1) / criticalImages.length) * 30);
              resolve(null);
            }, 3000);
          });
        });

        await Promise.all(imagePromises);
        setLoadingProgress(70);

        // Wait for fonts to load (if using custom fonts)
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }
        setLoadingProgress(85);

        // Minimum display time for smooth UX (500ms)
        await new Promise((resolve) => setTimeout(resolve, 500));
        setLoadingProgress(100);

        // Small delay before fade out
        await new Promise((resolve) => setTimeout(resolve, 200));
        
        // Start fade out
        setIsFading(true);
        
        // Remove from DOM after fade completes
        await new Promise((resolve) => setTimeout(resolve, 500));
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading resources:', error);
        // Still complete loading after timeout
        setLoadingProgress(100);
        setIsFading(true);
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }
    };

    waitForResources();
  }, []);

  return (
    <>
      {isLoading && (
        <Preloader progress={loadingProgress} isFading={isFading} />
      )}
      <AuthProvider>
        <ModalProvider>
          <Router>
          <div className="App">
            <Routes>
              <Route path="/ask-access" element={<AskForAccess />} />
              <Route path="/try-it-yourself" element={<TryItYourself />} />
              <Route path="/*" element={
                <>
                  <Header />
                  <div className="relative">
                    <Routes>
                      <Route path="/" element={<Hero />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/products" element={<Products />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/use-cases/:industryId" element={<IndustryPage />} />
                      <Route path="/products/:productId" element={<ProductPage />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/business-continuity-policy" element={<BusinessContinuityPolicy />} />
                    </Routes>
                  </div>
                  <Footer />
                  <Chatbot />
                  <ScrollToTop />
                </>
              } />
            </Routes>
          </div>
        </Router>
        </ModalProvider>
      </AuthProvider>
    </>
  );
}

export default App;